<div class="container tw-mx-auto tw-max-w-6xl tw-px-4 tw-py-8 knowledge-article">
  <!-- loading -->
  <ng-container *ngIf="isLoading">
    <app-skeleton [height]="2" width="60%" [marginBottom]="1.5"></app-skeleton>
    <app-skeleton *ngFor="let row of [1, 2, 3, 4, 5]" [marginBottom]="0.75"></app-skeleton>
  </ng-container>

  <!-- not found -->
  <div *ngIf="!isLoading && notFound" class="tw-py-16 tw-text-center" data-testid="article-not-found">
    <h1 class="p-text-h2 tw-mb-2">{{ 'learn.kcNotFoundTitle' | translate }}</h1>
    <p class="tw-text-body">
      {{ 'learn.kcNotFoundBody' | translate }}
      <a routerLink="/learn" class="tw-text-primary">{{ 'learn.backToLearn' | translate }}</a>
    </p>
  </div>

  <!-- redesigned 3-column reader: category tree · content · on-this-page TOC -->
  <div
    *ngIf="!isLoading && !notFound && article"
    data-testid="kc-reader"
    class="kc-reader tw-grid tw-grid-cols-1 tw-gap-7 lg:tw-grid-cols-[200px_1fr_190px]"
  >
    <!-- LEFT: category tree -->
    <nav data-testid="kc-reader-tree" class="kc-reader-tree tw-hidden lg:tw-block tw-text-sm">
      <p class="tw-mb-2 tw-text-[11px] tw-font-bold tw-uppercase tw-tracking-wider tw-text-gray-400">
        {{ 'learn.knowledgeCentre' | translate }}
      </p>
      <a
        routerLink="/learn"
        [class.kc-tree-active]="!isFaq"
        class="tw-block tw-rounded tw-px-2 tw-py-1.5 tw-text-gray-600 tw-no-underline hover:tw-bg-gray-50"
      >
        {{ 'learn.learnGuides' | translate }}
      </a>
      <a
        routerLink="/learn"
        [class.kc-tree-active]="isFaq"
        class="tw-block tw-rounded tw-px-2 tw-py-1.5 tw-text-gray-600 tw-no-underline hover:tw-bg-gray-50"
      >
        {{ 'learn.helpFaq' | translate }}
      </a>
      <p
        *ngIf="article.productLine"
        class="tw-mt-4 tw-text-[11px] tw-font-bold tw-uppercase tw-tracking-wider tw-text-gray-400"
      >
        {{ article.productLine | uppercase }}
      </p>
    </nav>

    <!-- CENTER: content -->
    <article class="kc-reader-main" data-testid="knowledge-article">
      <!-- #51 SEO: Article/TechArticle + BreadcrumbList JSON-LD, server-rendered into the page
           (present in view-source). NO FAQPage/HowTo (retired May 2026). -->
      <app-json-ld *ngIf="articleLd" [itemLd]="articleLd" data-testid="kc-article-ld"></app-json-ld>
      <app-json-ld *ngIf="breadcrumbLd" [itemLd]="breadcrumbLd" data-testid="kc-breadcrumb-ld"></app-json-ld>

      <!-- breadcrumb -->
      <nav
        data-testid="kc-reader-breadcrumb"
        class="tw-mb-4 tw-flex tw-flex-wrap tw-items-center tw-gap-1 tw-text-xs tw-text-gray-500"
      >
        <ng-container *ngFor="let crumb of breadcrumb; let last = last; let i = index">
          <span *ngIf="i > 0" class="tw-mx-1 tw-text-gray-300">&rsaquo;</span>
          <span [class.tw-font-semibold]="last" [class.tw-text-gray-700]="last">{{ crumb }}</span>
        </ng-container>
      </nav>

      <h1 class="p-text-h2 tw-mb-3">{{ article.title }}</h1>

      <!-- members-only badge -->
      <span
        *ngIf="article.gated"
        data-testid="members-badge"
        class="tw-mb-3 tw-inline-block tw-rounded tw-bg-gray-100 tw-px-2 tw-py-1 tw-text-xs tw-font-medium tw-text-gray-600"
      >
        {{ 'learn.membersOnly' | translate }}
      </span>

      <!-- author + last-updated -->
      <div
        data-testid="kc-reader-author"
        class="tw-mb-5 tw-flex tw-flex-wrap tw-items-center tw-gap-2 tw-text-xs tw-text-gray-500"
      >
        <span data-testid="kc-reader-author-name">{{ 'learn.byAuthor' | translate: { author: article.author || 'pitchIN Team' } }}</span>
        <ng-container *ngIf="article.updatedAt">
          <span class="tw-text-gray-300">&middot;</span>
          <span data-testid="kc-reader-updated">{{ 'learn.updatedOn' | translate: { date: (article.updatedAt | date: 'd MMM y') } }}</span>
        </ng-container>
      </div>

      <!-- rendered article body (headings carry injected ids for the TOC) -->
      <div
        data-testid="article-body"
        class="tw-prose tw-max-w-none tw-text-lg tw-text-body prose-headings:tw-text-black prose-p:tw-font-normal prose-a:tw-text-primary prose-a:tw-no-underline prose-ul:tw-mb-2.5 prose-ul:tw-mt-0 prose-img:tw-my-2.5 prose-img:tw-max-w-full prose-hr:tw-my-8"
        [innerHtml]="content"
      ></div>

      <!-- locked: teaser fade + sign-up to unlock -->
      <div
        *ngIf="article.locked"
        data-testid="unlock-prompt"
        class="tw-mt-6 tw-rounded-lg tw-border tw-border-gray-200 tw-bg-gray-50 tw-p-6 tw-text-center"
      >
        <h3 class="p-text-h4 tw-mb-2">{{ 'learn.unlockTitle' | translate }}</h3>
        <p class="tw-mb-4 tw-text-body">
          {{ 'learn.unlockBody' | translate }}
        </p>
        <a routerLink="/sign-up" class="btn btn-primary" data-testid="unlock-cta">{{ 'learn.unlockCta' | translate }}</a>
        <p class="tw-mt-3 tw-text-sm">
          {{ 'learn.alreadyMember' | translate }}
          <a routerLink="/login" class="tw-text-primary">{{ 'common.login' | translate }}</a>
        </p>
      </div>

      <!-- was this helpful? (shown when not locked); persists server-side -->
      <div
        *ngIf="!article.locked"
        data-testid="kc-reader-helpful"
        class="tw-mt-10 tw-border-t tw-border-gray-200 tw-pt-6"
      >
        <ng-container *ngIf="helpfulChoice === null">
          <span class="tw-mr-3 tw-font-medium tw-text-body">{{ 'learn.wasHelpful' | translate }}</span>
          <button
            type="button"
            (click)="voteHelpful('yes')"
            data-testid="helpful-yes"
            [disabled]="helpfulSubmitting"
            class="tw-mr-2 tw-rounded tw-border tw-border-gray-300 tw-px-4 tw-py-1 hover:tw-border-primary"
          >
            👍 {{ 'common.yes' | translate }}
          </button>
          <button
            type="button"
            (click)="voteHelpful('no')"
            data-testid="helpful-no"
            [disabled]="helpfulSubmitting"
            class="tw-rounded tw-border tw-border-gray-300 tw-px-4 tw-py-1 hover:tw-border-primary"
          >
            👎 {{ 'common.no' | translate }}
          </button>
        </ng-container>
        <p *ngIf="helpfulChoice !== null" class="tw-text-body" data-testid="helpful-thanks">
          {{ 'learn.helpfulThanks' | translate }}
        </p>
      </div>

      <!-- related Learn↔FAQ cross-links -->
      <div
        *ngIf="article.related?.length"
        data-testid="kc-reader-related"
        class="tw-mt-8"
      >
        <p class="tw-mb-3 tw-text-xs tw-font-bold tw-uppercase tw-tracking-wider tw-text-gray-400">
          {{ 'learn.related' | translate }}
        </p>
        <a
          *ngFor="let link of article.related"
          [routerLink]="relatedRoute(link)"
          data-testid="kc-reader-related-link"
          class="tw-mb-2 tw-flex tw-items-center tw-gap-2 tw-rounded-lg tw-border tw-border-gray-200 tw-px-3 tw-py-2.5 tw-text-sm tw-font-medium tw-text-gray-800 tw-no-underline hover:tw-border-primary hover:tw-bg-gray-50"
        >
          <span
            class="tw-rounded-full tw-px-2 tw-py-0.5 tw-text-[10px] tw-font-bold tw-uppercase"
            [class.tw-bg-blue-50]="link.type === 'learn'"
            [class.tw-text-blue-700]="link.type === 'learn'"
            [class.tw-bg-green-50]="link.type === 'faq'"
            [class.tw-text-green-700]="link.type === 'faq'"
          >
            {{ link.type === 'faq' ? 'FAQ' : 'Learn' }}
          </span>
          <span class="tw-flex-1">{{ link.title }}</span>
          <span *ngIf="link.gated" class="tw-text-xs tw-text-gray-400" data-testid="kc-reader-related-locked">
            {{ 'learn.membersShort' | translate }}
          </span>
        </a>
      </div>
    </article>

    <!-- RIGHT: on-this-page TOC -->
    <aside
      *ngIf="toc.length"
      data-testid="kc-reader-toc"
      class="kc-reader-toc tw-hidden lg:tw-block tw-self-start tw-text-sm lg:tw-sticky lg:tw-top-20"
    >
      <p class="tw-mb-2 tw-text-[11px] tw-font-bold tw-uppercase tw-tracking-wider tw-text-gray-400">
        {{ 'learn.onThisPage' | translate }}
      </p>
      <a
        *ngFor="let item of toc"
        [href]="'#' + item.id"
        (click)="scrollToSection(item.id, $event)"
        data-testid="kc-reader-toc-link"
        class="tw-block tw-border-l-2 tw-border-gray-200 tw-py-1 tw-text-gray-500 tw-no-underline hover:tw-text-primary"
        [class.tw-pl-3]="item.level === 2"
        [class.tw-pl-6]="item.level === 3"
      >
        {{ item.text }}
      </a>
    </aside>
  </div>
</div>
